import {
  Campaign as GraphQLResponseCampaigns,
  DashboardMode,
  DistributionChannel,
} from '@phrasee/phrasee-typings/Graphql/interfaces'
import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { gql } from 'graphql-request'
import moment from 'moment'

import { fetchGraphQL } from 'common/api'
import { CancelTokenSource, connectInternalApi } from 'common/api'
import {
  fetchUpliftReport,
  ReportLeveledResponse,
  ReportResponse,
} from 'common/api/reporting'
import { ResponseStatus } from 'common/interfaces/campaign'
import { ResponseProject } from 'features/projects/interface'

const twelveMonthsAgo = moment().subtract(12, 'months').toISOString()

interface CampaignsResponse {
  campaigns: ResponseCampaign[]
  count: number
}

interface CampaignMetric {
  count: number
}

export interface CampaignMetricsResponse {
  live: CampaignMetric
  completed: CampaignMetric
  scheduled: CampaignMetric
  needApproval: CampaignMetric
}

export type ProjectSummaryResponse = {
  email_projects: EmailProject[]
  push_projects?: PushProject[]
  sms_projects?: SmsProject[]
}

export type EmailProject = {
  id: string
  name: string
  number_of_campaigns?: number
  open_uplift?: number
  click_uplift?: number
  incremental_opens?: number
  incremental_clicks?: number
}

type SmsProject = {
  id: string
  name: string
  number_of_campaigns?: number
  click_uplift?: number
  incremental_clicks?: number
}

type PushProject = {
  id: string
  name: string
  number_of_campaigns?: number
  direct_open_uplift?: number
  influenced_open_uplift?: number
  incremental_direct_opens?: number
  incremental_influenced_opens?: number
}

export interface ContentKpiResponse {
  contentsNumber: number
  categories: DashboardCategory[]
}

interface DashboardCategory {
  label: string
  value: number
}

type DailyMessageResponse = {
  text: string
  date?: string
  emoji?: string | undefined
}

export interface ResponseCampaign
  extends Pick<
    GraphQLResponseCampaigns,
    '_id' | 'name' | 'status' | 'campaign_configuration' | 'user_id'
  > {
  id: string
  name: string
  project: { projectName: string }
  status: ResponseStatus
  user_id: string
  campaign_configuration: { distribution_channel: DistributionChannel }
}

export const fetchRecentlyCreated = function (
  page: number,
  pageSize: number,
  accountId: string
): Promise<CampaignsResponse> {
  const query = gql`
    query GetRecentlyCreated(
      $page: Int
      $pageSize: Int
      $sorting: CampaignSorting
      $filter: CampaignFilter
    ) {
      data: campaigns(
        page: $page
        pageSize: $pageSize
        sorting: $sorting
        filter: $filter
      ) {
        campaigns {
          id: _id
          name
          project {
            projectName: name
          }
          status
          user_id
          campaign_configuration {
            distribution_channel
          }
        }
      }
    }
  `

  const variables = {
    page,
    pageSize,
    sorting: {
      field: 'created',
      direction: 'DESC',
    },
    filter: {
      accountId,
    },
  }

  return fetchGraphQL<CampaignsResponse>({ query, variables })
}

export const fetchTodos = function ({
  page,
  pageSize,
  userId,
  accountId,
}): Promise<CampaignsResponse> {
  const query = gql`
    query GetTodo(
      $page: Int
      $pageSize: Int
      $sorting: CampaignSorting
      $filter: CampaignFilter
    ) {
      data: campaigns(
        page: $page
        pageSize: $pageSize
        sorting: $sorting
        filter: $filter
      ) {
        campaigns {
          id: _id
          name
          status
          user_id
          campaign_configuration {
            distribution_channel
          }
        }
      }
    }
  `

  const variables = {
    page,
    pageSize,
    sorting: {
      field: 'created',
      direction: 'DESC',
    },
    filter: {
      userId,
      accountId,
    },
  }

  return fetchGraphQL<CampaignsResponse>({ query, variables })
}

export const getCampaignMetrics = async (
  accountId: string
): Promise<CampaignMetricsResponse> => {
  const result = await connectInternalApi.get<CampaignMetricsResponse>(
    `v1/core/monorail/accounts/${accountId}/dashboard/campaigns`
  )
  return result.data
}

export const getContentKpi = async (
  accountId: string
): Promise<ContentKpiResponse> => {
  const result = await connectInternalApi.get<ContentKpiResponse>(
    `v1/core/content-gen/accounts/${accountId}/contents/reports/dashboard`
  )
  return result.data
}

export const getBroadcastProjects = async ({
  accountId,
  endDate,
  startDate,
}: {
  accountId: string
  endDate: string
  startDate: string
}) => {
  return connectInternalApi.get<{ data: ProjectSummaryResponse }>(
    `v1/core/reporting/reporting/accounts/${accountId}/report/summary`,
    {
      params: {
        reporting_level: 'project',
        distribution_type: 'broadcast',
        start_date: startDate,
        end_date: endDate,
      },
    }
  )
}

type QueryResponseType =
  | AxiosResponse<{ data: ProjectSummaryResponse }>
  | AxiosResponse<ReportLeveledResponse | ReportResponse>
  | null

export const useGetBroadcastMetricsQuery = (
  accountId: string,
  dashboardMode: DashboardMode | undefined,
  projectIds: string[]
) => {
  const endDate = moment().toISOString()
  const startDate = twelveMonthsAgo

  const query = useQuery<QueryResponseType>(
    ['broadcast_email_projects_metrics', accountId, projectIds],
    () => {
      if (dashboardMode === 'Incremental') {
        return getBroadcastProjects({ accountId, endDate, startDate })
      }
      return fetchUpliftReport({
        projectIds,
        accountId,
        endDate,
        startDate,
        upliftType: ['email_opens', 'email_clicks'],
        testingMethod: ['split_test_partial'],
        reportingLevel: 'project',
        periodType: 'aggregate',
      })
    }
  )
  return query
}

export async function fetchActiveProjectsByDistrubutionType(
  accountId: string,
  source: CancelTokenSource,
  distributionType: string
): Promise<ResponseProject[]> {
  const query = gql`
    query activeProjects($filter: ProjectFilter) {
      data: projects(filter: $filter) {
        _id
        name
        created
        last_updated
        deleted
        archived
        project_configuration {
          campaign_configurations {
            distribution_channel
            distribution_type
          }
        }
      }
    }
  `

  const variables = {
    filter: {
      accountId,
      includeDeleted: false,
      includeArchived: false,
      distributionType: distributionType,
    },
  }

  return fetchGraphQL<ResponseProject[]>(
    { query, variables },
    {
      cancelToken: source.token,
    }
  )
}

export const useActiveProjects = (
  accountId: string,
  source: CancelTokenSource,
  distributionType: string
) => {
  const { data, isError: hasGQLError } = useQuery(
    [`active_${distributionType}_projects`, accountId],
    async () =>
      await fetchActiveProjectsByDistrubutionType(
        accountId,
        source,
        distributionType
      ),
    {
      refetchOnWindowFocus: false,
      select: (data) =>
        data
          ?.filter(
            ({ project_configuration }) =>
              project_configuration?.campaign_configurations
                ?.distribution_channel === 'email'
          )
          ?.map(({ _id }) => _id),
    }
  )

  return { data, hasGQLError }
}

const getTriggerProjects = ({
  accountId,
  projectIds,
  endDate,
  startDate,
}: {
  accountId: string
  projectIds: string[]
  endDate: string
  startDate: string
}) => {
  return connectInternalApi.get<{
    data: ProjectSummaryResponse
  }>(`v1/core/reporting/reporting/accounts/${accountId}/report/summary`, {
    params: {
      project_ids: projectIds,
      reporting_level: 'project',
      distribution_type: 'AlwaysOn',
      start_date: startDate,
      end_date: endDate,
    },
  })
}

export const useGetTriggerMetricsQuery = (
  accountId: string,
  dashboardMode: DashboardMode | undefined,
  projectIds: string[]
) => {
  const endDate = moment().toISOString()
  const startDate = twelveMonthsAgo
  const query = useQuery<QueryResponseType>(
    ['trigger_email_projects_metrics', accountId, projectIds],
    () => {
      if (dashboardMode === 'Incremental') {
        return getTriggerProjects({ accountId, projectIds, endDate, startDate })
      }
      return fetchUpliftReport({
        projectIds,
        accountId,
        endDate,
        startDate,
        upliftType: ['email_opens', 'email_clicks'],
        testingMethod: ['split_test_partial'],
        reportingLevel: 'project',
        periodType: 'aggregate',
      })
    }
  )

  return query
}

export const getDailyWelcomeMessage = ({
  userFirstName,
  usersCurrentDate,
}: {
  userFirstName: string
  usersCurrentDate: string
}) => {
  return connectInternalApi.post<{ dailyMessage: DailyMessageResponse }>(
    `/v1/homepage/welcome-messages`,
    {
      userFirstName,
      usersCurrentDate,
    }
  )
}
